import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/api';

const BiddingModal = ({ show, onClose, jobId, isEditMode = false, existingBidData = null }) => {
  const { user } = useAuth();
  const [bidAmount, setBidAmount] = useState('');
  const [coverLetter, setCoverLetter] = useState('');
  const [selectedRoster, setSelectedRoster] = useState([]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditMode && existingBidData) {
      setBidAmount(existingBidData.bidAmount || '');
      setCoverLetter(existingBidData.coverLetter || '');
      // Note: Roster selection is not pre-filled as it adds complexity.
    }
  }, [isEditMode, existingBidData]);

  // TODO: Roster should be fetched from the backend if the user is a broker
  const mockRoster = [
    { id: 'worker-101', name: 'Rahim' },
    { id: 'worker-102', name: 'Karim' },
    { id: 'worker-103', name: 'Salam' },
  ];

  if (!show) {
    return null;
  }

  const handleRosterSelection = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setSelectedRoster(prev => [...prev, value]);
    } else {
      setSelectedRoster(prev => prev.filter(id => id !== value));
    }
  };

  const handleSubmitBid = async () => {
    if (!bidAmount) {
      setError('Please enter a bid amount.');
      return;
    }
    
    setError('');
    setIsSubmitting(true);

    const bidData = {
      jobId: isEditMode ? existingBidData.jobId : jobId,
      bidAmount: parseFloat(bidAmount),
      coverLetter,
      userId: user.id,
      role: user.role,
      ...(user.role === 'broker' && { roster: selectedRoster }),
    };

    try {
      let response;
      if (isEditMode) {
        // Assumes an endpoint for updating a bid
        response = await apiClient.put(`/bids/${existingBidData.id}`, bidData);
      } else {
        // The endpoint is assumed to be '/bids' for creating
        response = await apiClient.post('/bids', bidData);
      }
      
      if (response.status === 200 || response.status === 201) {
        alert(`Bid ${isEditMode ? 'updated' : 'submitted'} successfully!`);
        onClose(); // Close the modal on success
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An unexpected error occurred. Please try again.');
      console.error('Bid submission failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">{isEditMode ? 'Edit Your Bid' : 'Place Your Bid'}</h2>
        
        {/* Bid Amount */}
        <div className="mb-4">
          <label htmlFor="bidAmount" className="block text-gray-700 font-semibold mb-2">Your Bid Amount (BDT)</label>
          <input
            type="number"
            id="bidAmount"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-gray-700 focus:outline-none focus:border-blue-500"
            placeholder="e.g., 20000"
            disabled={isSubmitting}
          />
        </div>

        {/* Cover Letter */}
        <div className="mb-6">
          <label htmlFor="coverLetter" className="block text-gray-700 font-semibold mb-2">Cover Letter (Optional)</label>
          <textarea
            id="coverLetter"
            rows="4"
            value={coverLetter}
            onChange={(e) => setCoverLetter(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg text-gray-700 focus:outline-none focus:border-blue-500"
            placeholder="Tell the job poster why you're the best fit for this job."
            disabled={isSubmitting}
          ></textarea>
        </div>

        {/* Broker Roster Selection */}
        {user?.role === 'broker' && !isEditMode && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Include Workers from Roster</h3>
            <div className="space-y-2 max-h-32 overflow-y-auto border p-3 rounded-md">
              {mockRoster.map(worker => (
                <div key={worker.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`roster-${worker.id}`}
                    value={worker.id}
                    onChange={handleRosterSelection}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    disabled={isSubmitting}
                  />
                  <label htmlFor={`roster-${worker.id}`} className="ml-2 text-gray-800">{worker.name}</label>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="bg-gray-200 text-gray-700 font-bold py-2 px-6 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitBid}
            className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : (isEditMode ? 'Update Bid' : 'Submit Bid')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BiddingModal;
